<script setup>
import dayjs from 'dayjs'
import { computed, ref } from 'vue'
import { useUiStore } from '@/stores/UiStore'
import LoadDrawerStop from './LoadDrawerStop.vue'
import { UPDATE_TRUCK, UPDATE_DRIVER } from '@/constants/dispatch'
import DashedCheckMarkIcon from '@/assets/icons/dashed-check-mark.svg'

const props = defineProps({
    /**
     * The current location.
     *
     * @type {Object}
     */
    currentLocation: {
        required: true,
    },

    /**
     * The current stop id.
     *
     * @type {Number}
     */
    currentStopId: {
        type: Number,
        required: true,
    },
    /**
     * The index of the segment.
     *
     * @type {Number}
     */
    index: {
        type: Number,
        default: 0,
    },

    /**
     * The current load object to display.
     *
     * @type {Object}
     */
    load: {
        type: Object,
        default() {
            return {}
        },
    },

    /**
     * The segment to display.
     *
     * @type {Object}
     */
    segment: {
        type: Object,
        default: () => {},
    },
})

/**
 * The components store instances.
 */
const uiStore = useUiStore()

/**
 * The registered emits.
 */
const emits = defineEmits(['change:driver', 'change:truck'])

/**
 * The local refs.
 */
const panels = ref([])

/**
 * Get the segment's arrived stops.
 *
 * @return {Number}
 */
const arrivedStops = computed(() => {
    const arrived = props.segment?.stops.filter((stop) => {
        return !!stop.actual.arrive_at
    })

    return arrived.length
})

/**
 * Determine if the user can edit the truck.
 *
 * @return {Boolean}
 */
const canEditTruck = computed(() => {
    return props.segment?.stops[0]?.stop_type?.name !== 'Slipseat'
})

/**
 * Get the segment's drivers.
 *
 * @return {Array}
 */
const drivers = computed(() => {
    return props.segment?.drivers || []
})

/**
 * Get the first stop.
 *
 * @return {String}
 */
const firstStopCityState = computed(() => {
    const location = props.segment?.stops[0]?.location

    if (location) {
        return `${location.city}, ${location.state}`
    }

    return 'N/A'
})

/**
 * Determine if the segment has a carrier.
 *
 * @return {Boolean}
 */
const hasCarrier = computed(() => {
    return props.segment?.carrier
})

/**
 * Determine if the segment has incomplete stops.
 *
 * @return {Boolean}
 */
const hasIncompleteStops = computed(() => {
    const incompleteStops = props.segment?.stops.some((stop) => {
        return !stop.actual?.arrive_at
    })

    return incompleteStops && arrivedStops.value > 0
})

/**
 * Determine if the segment has late stops.
 *
 * @return {Boolean}
 */
const hasLateStops = computed(() => {
    return props.segment?.stops.some((stop) => {
        return dayjs(stop.actual?.arrive_at).isAfter(
            dayjs(stop.scheduled?.arrive_at)
        )
    })
})

/**
 * Determine if the segment is covered.
 *
 * @return {Boolean}
 */
const isCovered = computed(() => {
    const hasDrivers = props.segment?.drivers?.length > 0
    const hasCarrier = props.segment?.carrier

    return hasDrivers || hasCarrier
})

/**
 * Get the last stop.
 *
 * @return {String}
 */
const lastStopCityState = computed(() => {
    const index = props.segment?.stops.length - 1

    const location = props.segment?.stops[index]?.location

    if (location) {
        return `${location.city}, ${location.state}`
    }

    return 'N/A'
})

/**
 * Get the segment's total stops.
 *
 * @return {Number}
 */
const totalStops = computed(() => {
    return props.segment?.stops.length || 0
})

/**
 * Get the segment's trailers.
 *
 * @return {Array}
 */
const trailers = computed(() => {
    return props.segment?.trailers || []
})

/**
 * Get the segment's trucks.
 *
 * @return {Array}
 */
const truck = computed(() => {
    const truck = props.segment?.truck
    if (!props.segment || !truck) {
        return 'N/A'
    }

    return truck.name || 'N/A'
})

/**
 * Handle the change driver event.
 *
 * @return {void}
 */
const onChangeDriver = () => {
    uiStore.setActionSegmentId(UPDATE_DRIVER, props.segment.id)

    emits('change:driver', props.segment.id)
}

/**
 * Handle the change truck event.
 *
 * @return {void}
 */
const onChangeTruck = () => {
    uiStore.setActionSegmentId(UPDATE_TRUCK, props.segment.id)

    emits('change:truck', props.segment.id)
}
</script>

<template>
    <v-expansion-panel
        class="border !rounded-none mb-4"
        :value="segment.id"
        elevation="0"
    >
        <v-expansion-panel-title hide-actions class="flex !px-2 !py-3">
            <template v-slot="slotProps">
                <div class="flex justify-between w-full">
                    <div class="flex flex-col">
                        <div class="flex items-center">
                            <v-icon
                                size="sm"
                                class="border rounded border-gray-400 mr-3 h-6 w-6 text-gray-400"
                                :icon="
                                    slotProps.expanded
                                        ? 'far fa-angle-up'
                                        : 'far fa-angle-down'
                                "
                            ></v-icon>

                            <span class="font-bold mr-4">
                                Segment {{ index + 1 }}
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-col">
                        <div
                            class="flex h-6 items-center mb-2"
                            v-if="firstStopCityState && lastStopCityState"
                        >
                            {{ firstStopCityState }}
                            to
                            {{ lastStopCityState }}
                        </div>
                        <div class="flex h-6 items-center mb-2" v-else>
                            {{ firstStopCityState }}
                            {{ lastStopCityState }}
                        </div>

                        <div class="flex items-center mt-2">
                            <v-chip
                                v-if="isCovered"
                                color="gray"
                                class="text-base"
                                size="x-small"
                            >
                                Covered
                            </v-chip>
                            <v-chip
                                v-else
                                variant="tonal"
                                color="warning"
                                size="x-small"
                            >
                                Not Covered
                            </v-chip>
                            <v-chip
                                v-if="hasLateStops"
                                color="red"
                                class="ms-2 text-base"
                                size="x-small"
                            >
                                Late stops
                            </v-chip>
                        </div>
                    </div>

                    <div class="flex ml-auto h-6 items-center">
                        <dashed-check-mark-icon
                            v-if="hasIncompleteStops"
                            class="mr-2"
                        />

                        <span class="text-gray-600">
                            {{ arrivedStops }} of {{ totalStops }} arrived
                        </span>
                    </div>
                </div>
            </template>
        </v-expansion-panel-title>
        <v-expansion-panel-text class="overflow-auto border-t">
            <div class="p-6">
                <div class="flex justify-between">Assignment</div>

                <div class="border border-gray-300 rounded mb-2">
                    <div class="flex flex-col h-full">
                        <div class="flex justify-between h-full">
                            <div
                                v-if="!hasCarrier"
                                class="flex flex-col p-2 justify-start border-r w-full"
                            >
                                <div class="flex justify-between mb-2">
                                    <span class="text-sm text-gray-500">
                                        Driver(s)
                                    </span>
                                    <button
                                        @click.prevent="onChangeDriver"
                                        class="text-blue-600 text-sm"
                                    >
                                        Edit
                                    </button>
                                </div>

                                <div class="flex flex-col w-full mb-1">
                                    <span
                                        v-for="(driver, index) in drivers"
                                        :key="index"
                                        class="text-sm"
                                    >
                                        {{ driver.last_name }},
                                        {{ driver.first_name }}
                                    </span>
                                </div>
                            </div>
                            <div
                                v-if="!hasCarrier"
                                class="flex flex-col p-2 justify-start border-r w-full"
                            >
                                <div class="flex justify-between mb-2">
                                    <span class="text-sm text-gray-500">
                                        Truck
                                    </span>
                                    <button
                                        v-if="canEditTruck"
                                        @click.prevent="onChangeTruck"
                                        class="text-blue-600 text-sm"
                                    >
                                        Edit
                                    </button>
                                </div>
                                <div class="flex items-center mb-1">
                                    <span class="text-sm mr-2">
                                        {{ truck }}
                                    </span>
                                </div>
                            </div>
                            <div
                                v-else
                                class="flex flex-col p-2 justify-start border-r w-full"
                            >
                                <div class="flex justify-between mb-1">
                                    <span class="text-sm text-gray-500">
                                        Carrier
                                    </span>
                                    <button
                                        @click.prevent="onChangeDriver"
                                        class="text-blue-600 text-sm"
                                    >
                                        Edit
                                    </button>
                                </div>

                                <div class="flex flex-col w-full mb-1">
                                    <span>
                                        {{ segment.carrier.name }}
                                    </span>
                                </div>
                            </div>
                            <div class="flex flex-col p-2 h-full w-full">
                                <span class="text-sm mb-1 text-gray-500">
                                    Trailer(s)
                                </span>
                                <span
                                    v-for="(trailer, index) in trailers"
                                    :key="index"
                                >
                                    {{ trailer.name }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="start-segment">
                <div class="flex items-center">
                    <v-icon
                        class="mr-2"
                        size="x-small"
                        icon="fas fa-circle"
                    ></v-icon>

                    <div class="flex">
                        <span class="text-xs text-gray-500 mr-2"> Start </span>
                        <span
                            v-if="
                                segment.truck_start_location?.city &&
                                segment.truck_start_location?.state
                            "
                            class="text-xs mr-3"
                        >
                            {{ segment.truck_start_location.city }},
                            {{ segment.truck_start_location.state }}
                        </span>
                    </div>
                </div>

                <div class="flex flex-col items-center pr-0.5">
                    <a
                        :href="`/index.cfm?module=dispatch&template=dspLoad&intLoadId=${load.id}`"
                        class="text-blue-600 text-xs"
                    >
                        Set start location
                    </a>
                </div>
            </div>
            <v-expansion-panels v-model="panels">
                <load-drawer-stop
                    v-for="(stop, index) in segment.stops"
                    :key="index"
                    :stop="stop"
                    :load-id="load.id"
                    :currentLocation="currentLocation"
                    :current-stop-id="currentStopId"
                />
            </v-expansion-panels>
        </v-expansion-panel-text>
    </v-expansion-panel>
</template>
<style>
.start-segment {
    @apply bg-gray-100;

    align-items: center;
    display: flex;
    height: 3rem;
    justify-content: space-between;
    padding: 16px 24px;
}

.v-expansion-panel-text__wrapper {
    padding: 0 !important;
}

.v-expansion-panel__shadow {
    /* box-shadow: none; */
}

.v-expansion-panel:not(:first-child)::after {
    border-top: none;
}

.v-expansion-panel-title--active > .v-expansion-panel-title__overlay {
    opacity: 0;
}
</style>
